import { api, ApiError } from '@core/api/apiClient';
import { currencyCode } from '@shared/utils/formatMoney';
import type { PaymentProcessor } from '../state/types';

/** What a party-size change asks of the guest before it commits (the backend's `AdjustmentQuote::$requires`). */
export const GUARANTEE_REQUIRES = { NONE: 'none', ACCEPTANCE: 'acceptance', PAYMENT: 'payment', CARD: 'card' } as const;

export type GuaranteeRequires = (typeof GUARANTEE_REQUIRES)[keyof typeof GUARANTEE_REQUIRES];

/** Why the backend refused a party-size change (`GuaranteeAdjustmentRequiredException::reason()`). */
export const GUARANTEE_REFUSAL = {
  ACCEPTANCE_REQUIRED: 'acceptance_required',
  QUOTE_CHANGED: 'quote_changed',
  COMPOSITION_MISMATCH: 'composition_mismatch',
  PAYMENT_REQUIRED: 'payment_required',
  PAYMENT_IN_PROGRESS: 'payment_in_progress',
  PAYMENT_REQUIRES_ACTION: 'payment_requires_action',
  PAYMENT_DECLINED: 'payment_declined',
} as const;

export interface GuaranteeAdjustment {
  requires: GuaranteeRequires;
  previousFee: number;
  newFee: number;
  currency: string;
  /** The USD twins of a dolarized booking; 0 when the booking has none. */
  previousFeeUsd: number; newFeeUsd: number;
  /**
   * What a deposit increase charges now, in the currency the deposit was charged in (USD on a dolarized booking,
   * where `currency` stays the venue's), on the processor that charges it (the reservation's, not the venue's), and
   * the service fee percent the amount includes (0: none). Null when the backend sent none: deposit top-ups are
   * switched off there. A quote always names the processor; only the pay link's can name none (nobody can charge it).
   */
  outstanding: { amount: number; currency: string; processor: PaymentProcessor | null; surchargePercent: number } | null;
}

const REQUIRES_VALUES: ReadonlySet<string> = new Set(Object.values(GUARANTEE_REQUIRES));
export const CHARGING_PROCESSORS: ReadonlySet<string> = new Set<PaymentProcessor>(['stripe', 'mercadopago']);

/** A finite amount, sent as a number or a numeric string; null otherwise. */
export const toAmount = (value: unknown): number | null => {
  const parsed = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : null;
};

/** The adjustment the backend sent; null when it sent none or one this app cannot render. */
export function toGuaranteeAdjustment(raw: unknown): GuaranteeAdjustment | null {
  if (!raw || typeof raw !== 'object') return null;
  const {
    requires, previousFee, newFee, currency, previousFeeUsd, newFeeUsd,
    outstanding, outstandingCurrency, paymentProcessor, outstandingSurchargePercent,
  } = raw as Record<string, unknown>;
  const [previous, next, code] = [toAmount(previousFee), toAmount(newFee), currencyCode(currency as string)];
  if (typeof requires !== 'string' || !REQUIRES_VALUES.has(requires) || previous === null || next === null || code === null) return null;
  let owed: GuaranteeAdjustment['outstanding'] = null;
  if (outstanding != null) {
    // Every part is needed to charge the right card the right amount: a quote missing one (a backend before U3a-3) is not shown.
    const [amount, owedIn, surchargePercent] = [toAmount(outstanding), currencyCode(outstandingCurrency as string), toAmount(outstandingSurchargePercent)];
    if (amount === null || owedIn === null || surchargePercent === null || typeof paymentProcessor !== 'string' || !CHARGING_PROCESSORS.has(paymentProcessor)) return null;
    owed = { amount, currency: owedIn, processor: paymentProcessor as PaymentProcessor, surchargePercent };
  }
  return {
    requires: requires as GuaranteeRequires,
    previousFee: previous,
    newFee: next,
    currency: code,
    previousFeeUsd: toAmount(previousFeeUsd) ?? 0,
    newFeeUsd: toAmount(newFeeUsd) ?? 0,
    outstanding: owed,
  };
}

/** What the change charges the card now: a payment quote with an amount outstanding; null for anything else. */
export function chargeNow(adjustment: GuaranteeAdjustment | null): GuaranteeAdjustment['outstanding'] {
  const owed = adjustment?.requires === GUARANTEE_REQUIRES.PAYMENT ? adjustment.outstanding : null;
  return owed && owed.amount > 0 ? owed : null;
}

export type GuaranteeRefusal = { reason: string; guaranteeAdjustment: GuaranteeAdjustment | null };

/** The guarantee refusal a party-size change answered with, by the body's `reason` (the profile route also answers `409 false`); null otherwise. */
export function guaranteeRefusal(err: unknown): GuaranteeRefusal | null {
  if (!(err instanceof ApiError) || !err.body || typeof err.body !== 'object') return null;
  const { reason, guaranteeAdjustment } = err.body as Record<string, unknown>;
  if (typeof reason !== 'string') return null;
  return { reason, guaranteeAdjustment: toGuaranteeAdjustment(guaranteeAdjustment) };
}

/** What a party-size change would do to the guarantee, asked through the reservation-action link. */
export async function quotePartySizeByLink(token: string, partySize: number): Promise<GuaranteeAdjustment | null> {
  const body = { token, action: 'party_size_quote', party_size: partySize };
  const response = await api.post<{ guaranteeAdjustment?: unknown }>('/api/reservation-action', body);
  return toGuaranteeAdjustment(response?.guaranteeAdjustment);
}
